import React, { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LanguageContext, dict } from "@/i18n";
import { AuthProvider, Protected } from "@/auth";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import WorkshopBanner from "@/components/WorkshopBanner";
import DanceGroups from "@/components/DanceGroups";
import CompetitionGroups from "@/components/CompetitionGroups";
import EventsActivities from "@/components/EventsActivities";
import Marquee from "@/components/Marquee";
import TuitionBanner from "@/components/TuitionBanner";
import Manifesto from "@/components/Manifesto";
import Schedule from "@/components/Schedule";
import WhatsAppGrid from "@/components/WhatsAppGrid";
import Gallery from "@/components/Gallery";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import AuthPage from "@/pages/AuthPage";
import Workshop from "@/pages/Workshop";
import StudentDashboard from "@/pages/StudentDashboard";
import AdminPanel from "@/pages/AdminPanel";
import TeacherPanel from "@/pages/TeacherPanel";
import ResetPassword from "@/pages/ResetPassword";
import Legal from "@/pages/Legal";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ink text-white">
          <p className="font-mono text-sm text-zinc-400">Algo salió mal / Something went wrong</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Landing() {
  return (
    <>
      <main>
        <Hero />
        <WorkshopBanner />
        <TuitionBanner />
        <Marquee />
        <Manifesto />
        <DanceGroups />
        <Schedule />
        <CompetitionGroups />
        <Gallery />
        <EventsActivities />
        <WhatsAppGrid />
        <Contact />
      </main>
      <Footer />
    </>
  );
}

function App() {
  const [lang, setLang] = useState(() => localStorage.getItem("khouse-lang") || "es");

  useEffect(() => {
    localStorage.setItem("khouse-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2 });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  const value = useMemo(() => ({ lang, setLang, t: dict[lang] }), [lang]);

  return (
    <ErrorBoundary>
      <LanguageContext.Provider value={value}>
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <div className="bg-ink font-body text-white">
              <Navbar />
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/workshop" element={<Workshop />} />
                <Route path="/acceso" element={<AuthPage />} />
                <Route
                  path="/alumno"
                  element={
                    <Protected role="student">
                      <StudentDashboard />
                    </Protected>
                  }
                />
                <Route
                  path="/profesora"
                  element={
                    <Protected role={["teacher", "admin"]}>
                      <TeacherPanel />
                    </Protected>
                  }
                />
                <Route
                  path="/gestion"
                  element={
                    <Protected role="admin">
                      <AdminPanel />
                    </Protected>
                  }
                />
                <Route path="/restablecer" element={<ResetPassword />} />
                <Route path="/legal" element={<Legal />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster theme="dark" position="bottom-center" />
            </div>
          </AuthProvider>
        </BrowserRouter>
      </LanguageContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
